import React, { useRef, useEffect } from 'react'
import { IoPlaySharp, IoPauseSharp, IoStopSharp, IoClose, IoRefreshSharp } from 'react-icons/io5'
import { FiArrowDownRight } from 'react-icons/fi'
import { BiHelpCircle } from 'react-icons/bi'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/modules/rootReducer'
import PauseTimer from '../PauseTimer'
import { useTimer } from '../../../hooks/useTimer'
import { useTimerFullScreenTour } from '../hooks/useTimerFullScreenTour'
import {
  FullScreenOverlay,
  FullScreenContainer,
  CloseButton,
  MinimizeButton,
  HelpButton,
  DisciplineTitle,
  TimeDisplayLarge,
  StatusText,
  ProgressSectionLarge,
  ProgressLabelLarge,
  ProgressBarLarge,
  ProgressFillLarge,
  ProgressValueLarge,
  AddedTimeBadgeLarge,
  ControlsContainer,
  LargeIconButton,
  SeparatorLarge,
  AddTimeSection,
  AddTimeLabel,
  AddTimeButtons,
  IconButton,
} from './styles'

interface ITimerFullScreenProps {
  onStopClick: () => void
  onCloseClick: () => void
}

/**
 * Versão tela cheia do cronômetro
 * - Mesmo design do card mas expandido
 * - Controles maiores e mais espaçados
 * - Overlay escuro com blur
 */
const TimerFullScreen: React.FC<ITimerFullScreenProps> = ({ onStopClick, onCloseClick }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { isDarkMode } = useSelector((state: RootState) => state.theme)

  // Hook para gerenciar o mini-tour do TimerFullScreen
  const { startTour } = useTimerFullScreenTour({ isDarkMode })

  const {
    status,
    timeRemaining,
    progress,
    currentSession,
    formattedTime,
    addedTime,
    startTimer,
    pauseTimer,
    resumeTimer,
    restartTimer,
    addTime,
    toggleMode,
  } = useTimer()

  // Handler para ESC key - volta para modo card
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggleMode() // Volta para modo card ao invés de fechar
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggleMode])

  // Determina texto do status
  const getStatusText = () => {
    if (status === 'idle') return 'Pronto para iniciar'
    if (status === 'running') return 'Em andamento'
    if (status === 'paused') return 'Pausado'
    if (status === 'stopped') return 'Sessão finalizada'
    return ''
  }

  // Handler para play/pause
  const handlePlayPause = () => {
    if (status === 'idle' || status === 'stopped') {
      startTimer()
    } else if (status === 'running') {
      pauseTimer()
    } else if (status === 'paused') {
      resumeTimer()
    }
  }

  const accentColor = '#1edd0e'
  const subject = currentSession?.subject || 'Nenhuma sessão ativa'

  // Formata tempo decorrido para exibir progresso
  const getTimeElapsed = () => {
    const totalSeconds = (currentSession?.duration || 0) + addedTime
    // Se finalizado, mostra tempo total
    if (isFinished) {
      const minutes = Math.floor(totalSeconds / 60)
      return `${minutes}min`
    }
    const elapsedSeconds = totalSeconds - timeRemaining
    const minutes = Math.floor(elapsedSeconds / 60)
    return `${minutes}min`
  }

  const getTotalTime = () => {
    const totalSeconds = currentSession?.duration || 0
    const minutes = Math.floor(totalSeconds / 60)
    return `${minutes}min`
  }

  // Formata tempo extra em minutos para exibição
  const getAddedTimeDisplay = () => {
    if (addedTime <= 0) return null
    const minutes = Math.floor(addedTime / 60)

    if (minutes > 60) {
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return `+${hours}h ${remainingMinutes > 0 ? remainingMinutes + 'min' : ''}`
    } else {
      return `+${minutes}min`
    }
  }

  const isFinished = timeRemaining <= 0 && status !== 'idle'

  // Progresso deve ser 100% quando finalizado
  const displayProgress = isFinished ? 100 : progress

  return (
    <FullScreenOverlay>
      <FullScreenContainer ref={containerRef} accentColor={accentColor}>
        <HelpButton
          onClick={() => startTour(true)}
          title="Iniciar tour guiado"
          aria-label="Iniciar tour guiado do cronômetro"
        >
          <BiHelpCircle size={24} />
        </HelpButton>

        <MinimizeButton
          onClick={toggleMode}
          aria-label="Minimizar (ESC)"
          data-tour="timer-fullscreen-minimize"
        >
          <FiArrowDownRight />
        </MinimizeButton>

        <CloseButton
          onClick={onCloseClick}
          aria-label="Fechar cronômetro"
          data-tour="timer-fullscreen-close"
        >
          <IoClose />
        </CloseButton>

        <DisciplineTitle data-tour="timer-fullscreen-discipline">{subject}</DisciplineTitle>

        <TimeDisplayLarge data-tour="timer-fullscreen-time-display">
          {formattedTime}
        </TimeDisplayLarge>

        <StatusText data-tour="timer-fullscreen-status">{getStatusText()}</StatusText>

        {/* Mini timer de pausa */}
        <div data-tour="timer-fullscreen-pause-timer">
          <PauseTimer />
        </div>

        <ProgressSectionLarge>
          <ProgressLabelLarge>Progresso da sessão:</ProgressLabelLarge>
          <ProgressBarLarge data-tour="timer-fullscreen-progress-bar">
            <ProgressFillLarge progress={displayProgress} color={accentColor} />
          </ProgressBarLarge>
          <ProgressValueLarge data-tour="timer-fullscreen-progress-value">
            {getTimeElapsed()} / {getTotalTime()}
            {getAddedTimeDisplay() && (
              <AddedTimeBadgeLarge>{getAddedTimeDisplay()}</AddedTimeBadgeLarge>
            )}
          </ProgressValueLarge>
        </ProgressSectionLarge>

        <ControlsContainer>
          {/* Play/Pause - esconde quando finalizado */}
          {!isFinished && (
            <IconButton
              onClick={handlePlayPause}
              active={status === 'running'}
              disabled={status === 'idle' && !currentSession}
              aria-label={
                status === 'running' ? 'Pausar' : status === 'paused' ? 'Retomar' : 'Iniciar'
              }
              data-tour="timer-fullscreen-play-pause"
            >
              {status === 'running' ? <IoPauseSharp /> : <IoPlaySharp />}
            </IconButton>
          )}

          {/* Reiniciar - sempre com texto quando finalizado */}
          {isFinished && (
            <IconButton
              onClick={restartTimer}
              aria-label="Reiniciar"
              data-tour="timer-fullscreen-restart"
            >
              <IoRefreshSharp />
              <span style={{ marginLeft: '8px', fontSize: '0.95rem' }}>Reiniciar</span>
            </IconButton>
          )}

          {/* Parar/Finalizar - sempre com texto quando finalizado */}
          <IconButton
            onClick={onStopClick}
            disabled={status === 'idle'}
            aria-label={isFinished ? 'Finalizar' : 'Parar'}
            data-tour="timer-fullscreen-stop"
          >
            <IoStopSharp />
            {isFinished && (
              <span style={{ marginLeft: '8px', fontSize: '0.95rem' }}>Finalizar</span>
            )}
          </IconButton>
        </ControlsContainer>

        {/* Botões de incremento - apenas quando NÃO finalizado */}
        {(isFinished || status === 'running' || status === 'paused') && (
          <AddTimeSection data-tour="timer-fullscreen-add-time-section">
            <AddTimeLabel>Adicione tempo extra para estender a sessão:</AddTimeLabel>
            <AddTimeButtons data-tour="timer-fullscreen-add-time-buttons">
              <LargeIconButton onClick={() => addTime(60)} aria-label="Adicionar 1 minuto">
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+ 1min</span>
              </LargeIconButton>

              <LargeIconButton onClick={() => addTime(300)} aria-label="Adicionar 5 minutos">
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+ 5min</span>
              </LargeIconButton>

              <LargeIconButton onClick={() => addTime(600)} aria-label="Adicionar 10 minutos">
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+ 10min</span>
              </LargeIconButton>

              <LargeIconButton onClick={() => addTime(900)} aria-label="Adicionar 15 minutos">
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>+ 15min</span>
              </LargeIconButton>
            </AddTimeButtons>
          </AddTimeSection>
        )}
      </FullScreenContainer>
    </FullScreenOverlay>
  )
}

export default TimerFullScreen
